import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../utils/api';
import useIsMobile from '../utils/useIsMobile';

const STATUS_META = {
  completed:   { label: 'Completed',   bg: 'var(--status-resolved-bg)', color: 'var(--status-resolved-text)' },
  in_progress: { label: 'In progress', bg: 'var(--amber-100)',          color: '#92400e' },
  pending:     { label: 'Pending',     bg: 'var(--n-100)',              color: 'var(--text-secondary)' },
};

function fmtDay(d) { // 'YYYY-MM-DD' -> 'DD MMM YYYY'
  if (!d) return '—';
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function monthLabel(ym) { // 'YYYY-MM' -> 'Sep 2026'
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}
function istToday() {
  const now = new Date();
  const ist = new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60000);
  return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, '0')}-${String(ist.getDate()).padStart(2, '0')}`;
}

export default function MyTasks() {
  const isMobile = useIsMobile();
  const [mode, setMode] = useState('month');       // 'month' | 'day'
  const [months, setMonths] = useState([]);        // available YYYY-MM
  const [selMonths, setSelMonths] = useState([]);  // selected YYYY-MM
  const [day, setDay] = useState(istToday());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch('/daily-plans/my-tasks/months')
      .then(ms => { setMonths(ms); setSelMonths(ms.length ? [ms[0]] : [istToday().slice(0, 7)]); })
      .catch(e => setErr(e.message));
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const q = mode === 'day'
        ? `?date=${day}`
        : `?months=${(selMonths.length ? selMonths : [istToday().slice(0, 7)]).join(',')}`;
      setRows(await apiFetch(`/daily-plans/my-tasks${q}`));
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, [mode, day, selMonths]);

  useEffect(() => { load(); }, [load]);

  function toggleMonth(ym) {
    setSelMonths(s => s.includes(ym) ? s.filter(x => x !== ym) : [...s, ym]);
  }

  function exportCsv() {
    const esc = v => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s; };
    const header = ['Day', 'Task', 'Type', 'Ref', 'Priority', 'Status', 'Date added', 'Closed date', 'Remarks'];
    const lines = [header, ...rows.map(r => [
      r.plan_date, r.title, r.ticket_id ? 'Case' : 'Own task', r.ticket_number || '',
      r.priority || '', (STATUS_META[r.status] || {}).label || r.status,
      fmtDateTime(r.created_at), r.completed_at ? fmtDateTime(r.completed_at) : '', r.eod_remark || '',
    ])];
    const csv = lines.map(r => r.map(esc).join(',')).join('\r\n');
    const label = mode === 'day' ? day : (selMonths.join('_') || 'tasks');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `my_tasks_${label}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  }

  const done = rows.filter(r => r.status === 'completed').length;

  return (
    <div className="page" style={{ maxWidth: 1150 }}>
      <div className="page-header" style={{ alignItems: 'center' }}>
        <div>
          <div className="page-title">🗂 My Tasks</div>
          <div className="page-sub">Everything you planned — with added and closed dates</div>
        </div>
        <button className="btn btn-secondary" onClick={exportCsv} disabled={!rows.length}>📊 Export</button>
      </div>

      {err && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>{err}</div>}

      {/* Filters */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {[['month', 'By month'], ['day', 'By day']].map(([id, label]) => (
            <button key={id} onClick={() => setMode(id)} style={{
              padding: '5px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
              background: mode === id ? '#0B6623' : 'var(--n-100)', color: mode === id ? '#fff' : 'var(--text-secondary)' }}>
              {label}
            </button>
          ))}
        </div>

        {mode === 'month' ? (
          months.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic', margin: 0 }}>No task history yet.</p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {months.map(ym => {
                const on = selMonths.includes(ym);
                return (
                  <label key={ym} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                    border: `1.5px solid ${on ? '#0B6623' : 'var(--border-subtle)'}`,
                    background: on ? 'var(--action-bg)' : 'var(--bg-surface)', fontSize: 13, fontWeight: 600,
                    color: on ? 'var(--status-resolved-text)' : 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={on} onChange={() => toggleMonth(ym)} style={{ accentColor: '#0B6623', cursor: 'pointer' }} />
                    {monthLabel(ym)}
                  </label>
                );
              })}
            </div>
          )
        ) : (
          <input type="date" className="input" value={day} onChange={e => setDay(e.target.value)} style={{ width: 'auto', fontSize: 13 }} />
        )}
      </div>

      {/* Summary */}
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
        {rows.length} task{rows.length === 1 ? '' : 's'} · {done} completed
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><span className="spinner" /></div>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No tasks for this selection.</p>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(r => {
            const m = STATUS_META[r.status] || STATUS_META.pending;
            return (
              <div key={r.id} className="card" style={{ padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600 }}>{r.title}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: m.bg, color: m.color, whiteSpace: 'nowrap', height: 'fit-content' }}>{m.label}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {r.ticket_id ? <span style={{ fontFamily: 'monospace' }}>{r.ticket_number || 'Case'}</span> : 'Own task'} · {fmtDay(r.plan_date)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                  Added {fmtDateTime(r.created_at)}{r.completed_at ? ` · Closed ${fmtDateTime(r.completed_at)}` : ''}
                </div>
                {r.eod_remark && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>“{r.eod_remark}”</div>}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--n-50)' }}>
                {['Day', 'Task', 'Type', 'Status', 'Date added', 'Closed date', 'Remarks'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '.5px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const m = STATUS_META[r.status] || STATUS_META.pending;
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '10px 14px', fontSize: 12.5, whiteSpace: 'nowrap' }}>{fmtDay(r.plan_date)}</td>
                    <td style={{ padding: '10px 14px', fontSize: 13 }}>{r.title}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12 }}>
                      {r.ticket_id ? <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{r.ticket_number || 'Case'}</span> : <span style={{ color: 'var(--text-secondary)' }}>Own task</span>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 999, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>{m.label}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmtDateTime(r.created_at)}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{r.completed_at ? fmtDateTime(r.completed_at) : '—'}</td>
                    <td style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--text-muted)', maxWidth: 260 }}>{r.eod_remark || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
